import { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import WorkCard from "@/components/WorkCard";
import DonateModal from "@/components/DonateModal";
import api from "@/lib/api";
import { Search } from "lucide-react";

const GENRES = ["All", "Literary Fiction", "Screenplay", "Poetry", "Essay", "Mystery", "Sci-Fi", "Fantasy", "Memoir", "Other"];
const TYPES = ["All", "novel", "screenplay", "short_story", "poem", "essay", "other"];

export default function Browse() {
  const [works, setWorks] = useState([]);
  const [donateWork, setDonateWork] = useState(null);
  const [genre, setGenre] = useState("All");
  const [type, setType] = useState("All");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("top");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (genre !== "All") params.genre = genre;
    if (type !== "All") params.work_type = type;
    if (q.trim()) params.q = q.trim();
    params.sort = sort;
    const t = setTimeout(() => {
      api.get("/works", { params }).then(({ data }) => setWorks(data)).finally(() => setLoading(false));
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [genre, type, q, sort]);

  return (
    <div className="min-h-screen grain">
      <Nav />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <div className="chip mb-4">The archive</div>
        <h1 className="font-serif-display text-4xl sm:text-6xl text-[#F7F3EC] tracking-tight mb-4">Unfinished, for sale.</h1>
        <p className="font-serif-body text-lg text-[#A8A095] max-w-2xl mb-10">
          Every listing here is a work-in-progress. Read the excerpt, pay to unlock the vault, or tip the author anonymously.
        </p>

        <div className="mb-8 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[260px] max-w-lg">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E675E]" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search titles, excerpts, genres, pen names…"
              className="w-full bg-[#0D0C0A] border border-[rgba(217,180,114,0.15)] focus:border-[#D9B472] outline-none rounded-sm pl-10 pr-4 py-2.5 text-[#F7F3EC] font-mono-brass text-sm"
              data-testid="browse-search"
            />
          </div>
          <div className="flex gap-1 border border-[rgba(217,180,114,0.15)] rounded-sm p-1" data-testid="browse-sort">
            {[["top","Top rated"], ["new","Newest"]].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setSort(v)}
                className={`px-3 py-1.5 rounded-sm text-[10px] uppercase tracking-widest font-mono-brass ${sort === v ? "bg-[#D9B472] text-[#0D0C0A]" : "text-[#A8A095] hover:text-[#F7F3EC]"}`}
                data-testid={`browse-sort-${v}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-10" data-testid="browse-filters">
          <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass w-full mb-1">Genre</div>
          {GENRES.map((g) => (
            <button
              key={g}
              onClick={() => setGenre(g)}
              className={`px-3 py-1.5 rounded-sm border font-mono-brass text-xs uppercase tracking-widest transition-colors duration-200 ${
                genre === g ? "bg-[rgba(217,180,114,0.15)] border-[#D9B472] text-[#F7F3EC]" : "border-[rgba(217,180,114,0.15)] text-[#A8A095] hover:border-[rgba(217,180,114,0.3)]"
              }`}
              data-testid={`filter-genre-${g}`}
            >
              {g}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mb-12">
          <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass w-full mb-1">Form</div>
          {TYPES.map((t) => (
            <button
              key={t}
              onClick={() => setType(t)}
              className={`px-3 py-1.5 rounded-sm border font-mono-brass text-xs uppercase tracking-widest transition-colors duration-200 ${
                type === t ? "bg-[rgba(217,180,114,0.15)] border-[#D9B472] text-[#F7F3EC]" : "border-[rgba(217,180,114,0.15)] text-[#A8A095] hover:border-[rgba(217,180,114,0.3)]"
              }`}
              data-testid={`filter-type-${t}`}
            >
              {t.replace("_", " ")}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-[#6E675E] font-serif-body">Loading the archive…</p>
        ) : works.length === 0 ? (
          <p className="text-[#6E675E] font-serif-body">No fragments match those filters.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="browse-grid">
            {works.map((w, i) => <WorkCard key={w.id} work={w} onDonate={setDonateWork} index={i} />)}
          </div>
        )}
      </div>
      {donateWork && <DonateModal work={donateWork} onClose={() => setDonateWork(null)} />}
    </div>
  );
}
